import React, { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useSelfEmployee } from '@/lib/selfIdentity';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Palmtree, Calendar, AlertTriangle, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import BalanceExplanation from '@/components/ferie/BalanceExplanation';

/**
 * "Le mie ferie" — DIPENDENTE self-service page.
 * Uses the FROZEN AppContext (Ferie V1) as-is: it already binds
 * `scope.dipendenteId` to the logged-in employee and returns only self data.
 *
 * NO search, NO filters, NO other employees, NO dataset labels — those live
 * in the admin `Ferie.jsx` console (gated by `ferie.approve`).
 */
const STATO_BADGE = {
  richiesta:    { txt: 'In attesa',    cls: 'bg-amber-50 text-amber-800' },
  approvata:    { txt: 'Approvata',    cls: 'bg-emerald-50 text-brand-green' },
  programmata:  { txt: 'Programmata',  cls: 'bg-blue-50 text-blue-800' },
  goduta:       { txt: 'Goduta',       cls: 'bg-stone-100 text-stone-700' },
  rifiutata:    { txt: 'Rifiutata',    cls: 'bg-red-50 text-red-700' },
  annullata:    { txt: 'Annullata',    cls: 'bg-stone-100 text-stone-500 line-through' },
};

function StatoBadge({ stato }) {
  const s = STATO_BADGE[stato] || STATO_BADGE.richiesta;
  return <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${s.cls}`}>{s.txt}</span>;
}

function SummaryCard({ label, value, hint, tone, highlight, testid }) {
  const valueCls =
    tone === 'ok'   ? 'text-brand-green' :
    tone === 'warn' ? 'text-amber-700'   :
    tone === 'info' ? 'text-brand-blue'  : 'text-stone-900';
  return (
    <div data-testid={testid}
      className={`rounded-2xl border p-4 text-center ${highlight
        ? 'border-emerald-300 bg-emerald-50 shadow-sm'
        : 'border-stone-200 bg-white'}`}>
      <div className={`text-3xl md:text-4xl font-display font-semibold ${valueCls}`}>{value}<span className="text-base font-normal text-stone-500 ml-1">g</span></div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mt-1">{label}</div>
      {hint && <div className="text-[11px] text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

const fmtDay = (d) => d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
const round1 = (n) => Math.round((n || 0) * 10) / 10;

export default function FerieMie() {
  const {
    saldoOf, periodi, ANNO_CORRENTE, configOf,
    reloadDataset, previewRichiesta,
  } = useApp();
  // AUTHORITATIVE self-identity: server-side employees.auth_user_id = current
  // auth user. NEVER dipendentiInScope[0] / d1 / any positional fallback.
  const { self, isSimulated, notLinked } = useSelfEmployee();
  const [openReq, setOpenReq] = useState(false);
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  const [note, setNote] = useState('');

  // All hooks MUST be declared before any early return so they run in a stable
  // order across renders (react-hooks/rules-of-hooks). We guard with `?.` and
  // fall back to safe defaults when `self` is null.
  const saldo = useMemo(() => self
    ? (saldoOf(self.id) || { maturate: 0, godute: 0, programmate: 0, disponibile: 0, riportoPrecedente: 0, rettifiche: 0, mesePerMese: [] })
    : { maturate: 0, godute: 0, programmate: 0, disponibile: 0, riportoPrecedente: 0, rettifiche: 0, mesePerMese: [] },
  [self, saldoOf]);
  const cfg = self ? configOf(self.id) : null;
  const eoyDate = useMemo(() => new Date(ANNO_CORRENTE, 11, 31), [ANNO_CORRENTE]);
  const saldoEOY = useMemo(
    () => (self ? (saldoOf(self.id, eoyDate) || saldo) : saldo),
    [saldoOf, self, eoyDate, saldo],
  );
  const baseline = cfg?.baseline || null;

  if (notLinked && !isSimulated) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center" data-testid="ferie-mie-nolink">
        <div className="w-14 h-14 rounded-2xl bg-stone-100 text-stone-500 flex items-center justify-center mx-auto mb-4">
          <ShieldOff className="w-6 h-6" />
        </div>
        <h1 className="font-display text-xl font-semibold text-brand-navy">Profilo dipendente non collegato</h1>
        <p className="mt-2 text-sm text-stone-500">
          Il tuo account non risulta ancora associato a un profilo dipendente. Contatta il tuo responsabile per completare l'associazione.
        </p>
      </div>
    );
  }
  if (!self) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center text-stone-500 text-sm" data-testid="ferie-mie-nobind">
        Il tuo profilo dipendente non è ancora collegato. Chiedi al tuo responsabile di completare l'associazione.
      </div>
    );
  }

  const miei = periodi
    .filter(p => p.dipendenteId === self.id)
    .sort((a, b) => b.dal.localeCompare(a.dal));

  const inAttesa = miei.filter(p => p.stato === 'richiesta');
  const approvate = miei.filter(p => ['approvata', 'programmata'].includes(p.stato));
  const rifiutate = miei.filter(p => p.stato === 'rifiutata');
  const godute = miei.filter(p => p.stato === 'goduta');

  const preview = dal && al ? previewRichiesta(self.id, dal, al) : null;

  const puntoDiPartenza = round1(saldo.disponibile);
  const previstoEoy = round1(saldoEOY.disponibile);

  const submit = async () => {
    if (!dal || !al) return toast.error('Compila le date');
    if (new Date(al) < new Date(dal)) return toast.error("La data finale è precedente all'iniziale");
    try {
      await apiFetch('/api/ferie/analisi/periodi', {
        method: 'POST',
        body: JSON.stringify({
          dipendente_id: self.id,
          dal,
          al,
          stato: 'richiesta',
          anno_competenza: Number(dal.slice(0, 4)),
          note: note || null,
        }),
      });
      await reloadDataset();
      toast.success('Richiesta inviata al tuo responsabile');
      setOpenReq(false); setDal(''); setAl(''); setNote('');
    } catch (error) {
      toast.error(error.message || 'Invio non riuscito');
    }
  };

  return (
    <div className="space-y-6" data-testid="ferie-mie-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Area personale · Anno {ANNO_CORRENTE}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy flex items-center gap-2">
            <Palmtree className="w-7 h-7 text-brand-green" /> Le mie ferie
          </h1>
          <p className="mt-1 text-stone-500 text-sm">{self.reparto || self.displayName || `${self.nome || ''} ${self.cognome || ''}`.trim()}</p>
        </div>
        <Dialog open={openReq} onOpenChange={setOpenReq}>
          <DialogTrigger asChild>
            <Button data-testid="btn-ferie-mie-new" className="rounded-full gap-2 bg-brand-green hover:bg-brand-green-hover text-white">
              <Plus className="w-4 h-4" /> Nuova richiesta
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Richiedi ferie</DialogTitle>
              <DialogDescription>Il tuo responsabile riceverà una notifica appena invierai.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-stone-600">Dal</label>
                  <Input data-testid="ferie-mie-dal" type="date" value={dal} onChange={(e) => setDal(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-stone-600">Al</label>
                  <Input data-testid="ferie-mie-al" type="date" value={al} onChange={(e) => setAl(e.target.value)} className="mt-1" />
                </div>
              </div>
              {preview && (
                <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Giorni richiesti</span>
                    <span className="font-semibold text-stone-900">{preview.giorni}g</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-stone-600">Saldo dopo</span>
                    <span className={`font-semibold ${preview.saldoDopo < 0 ? 'text-red-600' : 'text-brand-green'}`}>{preview.saldoDopo}g</span>
                  </div>
                  {preview.avvisi.length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-stone-200">
                      {preview.avvisi.map((a, i) => (
                        <div key={i} className={`text-xs flex items-start gap-1.5 ${a.tipo === 'error' ? 'text-red-700' : 'text-amber-800'}`}>
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a.msg}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-stone-600">Note (opzionale)</label>
                <Textarea data-testid="ferie-mie-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenReq(false)}>Annulla</Button>
              <Button data-testid="btn-ferie-mie-submit" onClick={submit} className="bg-brand-green hover:bg-brand-green-hover rounded-full">Invia richiesta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="ferie-mie-saldo">
        <div className="text-xs uppercase tracking-widest text-stone-400 font-bold mb-4">Riepilogo {ANNO_CORRENTE}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard testid="summary-disponibili-oggi"
            label="Giorni disponibili" value={puntoDiPartenza}
            tone="ok" highlight
            hint="Saldo disponibile alla data di riferimento" />
          <SummaryCard testid="summary-godute"
            label="Godute"        value={round1(saldo.godute)} />
          <SummaryCard testid="summary-programmate"
            label="Programmate"   value={round1(saldo.programmate)} tone="warn" />
          <SummaryCard testid="summary-previsti-eoy"
            label={`Proiezione al 31/12/${ANNO_CORRENTE}`} value={previstoEoy}
            tone="info"
            hint="Saldo disponibile proiettato a fine anno" />
        </div>

        <BalanceExplanation
          balance={saldoEOY}
          baseline={baseline}
          year={ANNO_CORRENTE}
          projection
          policy={saldoEOY.policy}
          testid="ferie-mie-balance-explanation"
        />
      </div>

      <Sezione titolo="Le mie richieste — In attesa" periodi={inAttesa} testid="sezione-attesa" empty="Nessuna richiesta in attesa." />
      <Sezione titolo="Approvate / programmate" periodi={approvate} testid="sezione-approvate" empty="Nessun periodo pianificato." />
      {rifiutate.length > 0 && (
        <Sezione titolo="Rifiutate" periodi={rifiutate} testid="sezione-rifiutate" empty="" />
      )}
      <Sezione titolo={`Storico personale · ${ANNO_CORRENTE}`} periodi={godute} testid="sezione-godute" empty="Nessun periodo goduto quest'anno." />
    </div>
  );
}

function Sezione({ titolo, periodi, testid, empty }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3.5 border-b border-stone-100 flex items-center justify-between">
        <div className="font-display font-semibold text-stone-900 tracking-tight">{titolo}</div>
        <span className="text-xs text-stone-400">{periodi.length}</span>
      </div>
      <div className="divide-y divide-stone-100">
        {periodi.length === 0 && empty ? (
          <div className="px-5 py-4 text-sm text-stone-500 text-center">{empty}</div>
        ) : (
          periodi.map((p) => (
            <div key={p.id} data-testid={`ferie-mie-periodo-${p.id}`} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-stone-800 min-w-0">
                <Calendar className="w-4 h-4 text-stone-400 shrink-0" />
                <span className="truncate">{p.dal} → {p.al}</span>
                <span className="text-xs text-stone-500 shrink-0">· {p.giorni}g</span>
              </div>
              <StatoBadge stato={p.stato} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
